import React, { FormEvent, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import Button from "../../components/ui/Button";
import { tenantAppTitle } from "../../shared/tenantBranding";
import {
  acceptTenantInvitation,
  fetchTenantInvitation,
  TenantInvitationDetails,
} from "../../shared/tenantInvitationApi";

const MIN_PASSWORD = 12;

export default function AcceptTenantInvitation() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";
  const [details, setDetails] = useState<TenantInvitationDetails | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!token) {
      setError("This invitation link is incomplete.");
      setLoading(false);
      return;
    }

    fetchTenantInvitation(token)
      .then((response) => setDetails(response.data))
      .catch((caught) =>
        setError(
          caught.response?.data?.message ??
            "This invitation is invalid or has expired.",
        ),
      )
      .finally(() => setLoading(false));
  }, [token]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");

    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirmation) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await acceptTenantInvitation({
        token,
        password,
        password_confirmation: confirmation,
      });
      setSuccess(response.data.message);
    } catch (caught: any) {
      setError(
        caught.response?.data?.errors?.password?.[0] ??
          caught.response?.data?.message ??
          "Your account could not be activated.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="h-full">
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <h1 className="mt-5 text-center text-3xl font-bold leading-9 tracking-tight text-gray-900">
            {tenantAppTitle(null)}
          </h1>
          <h2 className="mt-5 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            Activate your account
          </h2>
          {details ? (
            <p className="mt-3 text-center text-sm text-gray-600">
              {details.administrator_name}, create your password for{" "}
              <strong>{details.company_name}</strong>.
            </p>
          ) : null}
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          {loading ? (
            <p className="text-center text-sm text-gray-600">
              Checking invitation…
            </p>
          ) : success ? (
            <div className="space-y-5">
              <div className="rounded-md bg-green-50 p-4 text-sm text-green-800">
                {success}
              </div>
              {details ? (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
                  <p className="font-semibold">Mobile sign-in</p>
                  <p className="mt-2">
                    Company domain: {details.tenant_domain}
                  </p>
                  <p>Username: {details.username}</p>
                </div>
              ) : null}
              <Link
                to="/login"
                className="block text-center font-semibold text-[var(--color-brand-700)] hover:text-[var(--color-brand-900)]"
              >
                Sign in to QA Trace
              </Link>
            </div>
          ) : details ? (
            <form className="space-y-6" onSubmit={submit}>
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Email
                </label>
                <p className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
                  {details.email}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Company domain
                  </label>
                  <p className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
                    {details.tenant_domain}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900">
                    Mobile username
                  </label>
                  <p className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
                    {details.username}
                  </p>
                </div>
              </div>
              <div>
                <label
                  htmlFor="invitation-password"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Password
                </label>
                <input
                  id="invitation-password"
                  type="password"
                  autoComplete="new-password"
                  className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-[var(--color-brand-700)] sm:text-sm sm:leading-6"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={submitting}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Minimum {MIN_PASSWORD} characters.
                </p>
              </div>
              <div>
                <label
                  htmlFor="invitation-password-confirmation"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Confirm password
                </label>
                <input
                  id="invitation-password-confirmation"
                  type="password"
                  autoComplete="new-password"
                  className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-[var(--color-brand-700)] sm:text-sm sm:leading-6"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  disabled={submitting}
                />
              </div>
              {error ? (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                  {error}
                </div>
              ) : null}
              <Button
                type="submit"
                disabled={submitting}
                loading={submitting}
                variant="primary"
                size="md"
                fullWidth
              >
                Activate account
              </Button>
            </form>
          ) : (
            <div className="space-y-5">
              <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
                {error}
              </div>
              <p className="text-center text-sm text-gray-600">
                Ask your QA Trace contact to resend the invitation.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
